import React from 'react';
import {
  Factory,
  Hammer,
  Cable,
  Flame,
  Pill,
  FlaskConical,
  Fuel,
  Thermometer,
  Droplets,
  Boxes,
  Cpu,
  Zap,
  Bot,
  Radio,
  Activity,
  Gauge,
  Settings,
  Shield,
  Wind,
  Sun,
  Layers,
  Wrench,
  CircuitBoard,
  Truck,
  HardHat,
  LucideProps,
} from 'lucide-react';

export interface IndustryIconDefinition {
  id: string;
  name: string;
  desc: string;
  category: 'Heavy & Metals' | 'Electrical & Cable' | 'Chemical & Process' | 'Energy & Utilities' | 'Logistics & Safety' | 'General Industry';
}

export const INDUSTRY_ICONS_LIBRARY: IndustryIconDefinition[] = [
  // Heavy & Metals
  { id: 'steel', name: 'Iron & Steel Plants', desc: 'Rolling mill, continuous casting & heavy forging', category: 'Heavy & Metals' },
  { id: 'factory', name: 'Heavy Manufacturing', desc: 'Continuous production plants & heavy machinery', category: 'Heavy & Metals' },
  { id: 'hammer', name: 'Foundry & Metal Forming', desc: 'Drop forging, presses & billet shearing', category: 'Heavy & Metals' },
  { id: 'furnace', name: 'Industrial Furnaces', desc: 'Burner management & pyrometry heat treatment', category: 'Heavy & Metals' },

  // Electrical & Cable
  { id: 'cable', name: 'Wire, Cable & Fiber', desc: 'High-speed extrusion, rewinding & capstans', category: 'Electrical & Cable' },
  { id: 'cpu', name: 'PLC & Automation Systems', desc: 'Deterministic micro-controllers & S7-1500 logic', category: 'Electrical & Cable' },
  { id: 'zap', name: 'Power & Substation', desc: 'Sinamics drives, switchgear & heavy busbars', category: 'Electrical & Cable' },
  { id: 'circuit-board', name: 'Embedded Electronics', desc: 'Drive control units & bus communications', category: 'Electrical & Cable' },
  { id: 'bot', name: 'Robotics & Pick-Place', desc: 'Articulated arms, palletizers & automated cells', category: 'Electrical & Cable' },

  // Chemical & Process
  { id: 'chemical', name: 'Chemical Processing', desc: 'Exothermic reactors, dosing & ultrasonic tanks', category: 'Chemical & Process' },
  { id: 'pharma', name: 'Pharmaceutical & API', desc: '21 CFR Part 11 batching, granulators & cleanrooms', category: 'Chemical & Process' },
  { id: 'rubber', name: 'Rubber & Polymers', desc: 'Internal Banbury mixers & vulcanization calenders', category: 'Chemical & Process' },
  { id: 'water', name: 'Water & Effluent Treatment', desc: 'Clarifier basins, pumping & dosing SCADA', category: 'Chemical & Process' },

  // Energy & Utilities
  { id: 'oil-gas', name: 'Oil & Gas Manifolds', desc: 'Offshore rigs, pipelines, hazardous IP55 zones', category: 'Energy & Utilities' },
  { id: 'wind', name: 'Wind & Clean Airflow', desc: 'Turbines, HVAC cleanroom differential loops', category: 'Energy & Utilities' },
  { id: 'sun', name: 'Solar & Renewable Energy', desc: 'Inverters, tracking arrays & green tech', category: 'Energy & Utilities' },
  { id: 'radio', name: 'Telemetry & Wide-Area SCADA', desc: 'Remote telemetry units (RTU) & OPC-UA gateways', category: 'Energy & Utilities' },

  // Logistics & Safety
  { id: 'material-handling', name: 'Material Handling', desc: 'Gantry cranes, automated conveyors & AGVs', category: 'Logistics & Safety' },
  { id: 'boxes', name: 'Packaging & Warehouse', desc: 'High-speed packaging, cartoning & sortation', category: 'Logistics & Safety' },
  { id: 'shield', name: 'SIL-3 Safety Systems', desc: 'Emergency trip shutdown, burner fail-safes', category: 'Logistics & Safety' },
  { id: 'hard-hat', name: 'Site Commissioning', desc: 'Field installation, safety & SAT protocols', category: 'Logistics & Safety' },

  // General Industry
  { id: 'settings', name: 'Machine Engineering', desc: 'Precision gearing, torque loops & synchronized drives', category: 'General Industry' },
  { id: 'gauge', name: 'Process Instrumentation', desc: 'Pressure, flow, temperature & velocity sensors', category: 'General Industry' },
  { id: 'activity', name: 'High-Speed Telemetry', desc: 'Vibration monitoring, OEE & predictive uptime', category: 'General Industry' },
  { id: 'wrench', name: 'Retrofit & Modernization', desc: 'Legacy drive migration & cabinet refurbishment', category: 'General Industry' },
  { id: 'layers', name: 'Control Panel Assembly', desc: 'IP55 type-tested MCC & VFD enclosures', category: 'General Industry' },
];

const INDUSTRY_ICONS_MAP: Record<string, React.ComponentType<LucideProps>> = {
  steel: Hammer,
  factory: Factory,
  hammer: Hammer,
  furnace: Thermometer,
  thermometer: Thermometer,
  cable: Cable,
  cpu: Cpu,
  zap: Zap,
  'circuit-board': CircuitBoard,
  bot: Bot,
  chemical: FlaskConical,
  flask: FlaskConical,
  pharma: Pill,
  pill: Pill,
  rubber: Flame,
  flame: Flame,
  water: Droplets,
  droplets: Droplets,
  'oil-gas': Fuel,
  fuel: Fuel,
  wind: Wind,
  sun: Sun,
  radio: Radio,
  'material-handling': Boxes,
  boxes: Boxes,
  shield: Shield,
  'hard-hat': HardHat,
  settings: Settings,
  gauge: Gauge,
  activity: Activity,
  wrench: Wrench,
  layers: Layers,
  truck: Truck,
};

export const renderIndustryIconById = (
  iconId?: string,
  props?: { className?: string; style?: React.CSSProperties }
): React.ReactNode => {
  const safeId = (iconId || '').trim();
  const className = props?.className || 'w-5 h-5';

  // Check if it's an uploaded image, SVG URL, or data URL
  if (
    safeId.startsWith('data:') ||
    safeId.startsWith('http://') ||
    safeId.startsWith('https://') ||
    safeId.startsWith('/')
  ) {
    return (
      <img
        src={safeId}
        alt="Industry Icon"
        className={`${className} object-contain`}
        style={props?.style}
      />
    );
  }

  const normalized = safeId.toLowerCase().replace(/\s+/g, '-');
  const Comp = INDUSTRY_ICONS_MAP[normalized] || INDUSTRY_ICONS_MAP[safeId] || Factory;
  return <Comp className={className} style={props?.style} strokeWidth={1.5} />;
};
